import PruebaGratis from "./PruebaGratis";
import { WizardEntrada } from "./PrimerosPasos";
import React, { useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { WhatsAppUnreadProvider, useWhatsAppUnread } from "../contexts/WhatsAppUnreadContext";
import { ConsumoProvider } from "../contexts/ConsumoContext";
import { AvisoPausa } from "./ConsumoSofi";
import { usePlanActual } from "../lib/planes";
import ClinicSwitcher from "./ClinicSwitcher";
import DemoBanner from "./DemoBanner";
import DemoExpiredModal from "./DemoExpiredModal";
import { PlanVigenteGuard, AvisoGracia } from "./PlanVigente";
import SuritLogo from "./brand/SuritLogo";
import {
  LayoutDashboard,
  MessageCircle,
  Calendar,
  Users,
  Settings,
  FileText,
  FolderOpen,
  BarChart3,
  FileSpreadsheet,
  CheckSquare,
  LogOut,
  Bell,
  Search,
  ChevronDown,
  Menu,
  X,
  Stethoscope,
  Wallet,
  UserCog,
  Receipt,
} from "lucide-react";

// 🔴 `cap` NO es una segunda copia del catalogo de capacidades: es el mapa
//    PANTALLA -> CAPACIDAD, que sólo puede vivir aqui porque sólo aqui se sabe qué
//    endpoint carga cada pantalla. El catálogo (los 9 códigos con su nombre y su orden)
//    sigue llegando del backend y no se escribe en JavaScript en ningún sitio.
//
//    Cada valor se DERIVÓ de `capacidades.MATRIZ` del backend consultando la declaración
//    del endpoint principal de cada pantalla, no de un criterio propio. Por ejemplo:
//      GET /api/dashboard/kpis      -> agenda        (Dashboard)
//      GET /api/metrics/overview    -> agenda        (Métricas)
//      GET /api/analytics/summary   -> agenda        (Analítica)
//      GET /api/recovery/patients   -> pacientes     (Recuperación)
//      GET /api/templates           -> whatsapp      (Plantillas: son textos para la bandeja,
//                                                     NO plantillas aprobadas por Meta, DT.90)
//      GET /api/documents           -> configuracion (Documentos, los de la clínica)
//      GET /api/onboarding          -> configuracion (Onboarding Surit)
//
//    ⚠️ Doctores se mapea a `agenda` y NO a `configuracion`, aunque parezca lo contrario:
//       `GET /api/doctors` -- lo que carga la pantalla -- exige `agenda`; lo que exige
//       `configuracion` es CREAR, EDITAR y BORRAR un médico. Con `agenda` la pantalla se
//       abre y se lee, así que esconderla escondería algo utilizable. Que los botones de
//       escritura respondan 403 es del backend, no de este mapa.
//
//    Si mañana una pantalla cambia de capacidad en el backend, este mapa se queda viejo y
//    escondería un menú que sí se puede usar. Por eso `pages/Colaboradores.js` contrasta
//    estos códigos contra el catálogo REAL y avisa si alguno dejó de existir --
//    un mapa que nadie verifica es el defecto de DT.235/DT.247/DT.248 otra vez.
const nav = [
  { to: "/app", icon: LayoutDashboard, label: "Dashboard", end: true, cap: "agenda" },
  { to: "/app/metricas", icon: BarChart3, label: "Métricas", cap: "agenda" },
  // `requiereSofi` (TEN.4): no se pinta en un plan sin asistente. Ver `usePlanActual`.
  { to: "/app/inbox", icon: MessageCircle, label: "Bandeja WhatsApp", cap: "whatsapp", requiereSofi: true },
  { to: "/app/agenda", icon: Calendar, label: "Agenda", cap: "agenda" },
  { to: "/app/doctores", icon: Stethoscope, label: "Doctores", cap: "agenda" },
  { to: "/app/pacientes", icon: Users, label: "Pacientes", cap: "pacientes" },
  // 🔴 COL.11 -- "Recuperación" RETIRADA del menú (15 -> 14 entradas). Su funcionalidad
  //    se retiró en la auditoría de copy del 12 Sep 2026 (DT.234): las cuatro acciones no
  //    tenían backend y la peor -"Enviar"- decía "Seguimiento enviado" mientras el servidor
  //    sólo marcaba `recovered=true` SIN CONTACTAR A NADIE; hoy `POST /recovery/send`
  //    responde 501. Una entrada de menú a una pantalla en "Próximamente" ofrece una
  //    función que no existe, que es justo lo que aquella auditoría vino a quitar.
  //    ⚠️ La RUTA y la pantalla se conservan (`App.js`): la sección se va a construir, y
  //       retirar la ruta obligaría a rehacerla desde cero. Lo que se retira es la oferta.
  //    ⚠️ "Analítica" se queda -decisión de Jesús- aunque comparta origen con aquella
  //       auditoría: sus métricas fabricadas ya se retiraron y lo que muestra hoy se calcula.
  // `soloReal`: no se pinta en una sesión de demo pública. Ver el filtro de abajo.
  { to: "/app/gastos", icon: Wallet, label: "Gastos", soloReal: true, cap: "gastos" },
  { to: "/app/reportes", icon: FileSpreadsheet, label: "Reportes", soloReal: true, cap: "reportes" },
  { to: "/app/asistente", icon: Settings, label: "Asistente IA", cap: "configuracion", requiereSofi: true },
  { to: "/app/plantillas", icon: FileText, label: "Plantillas", cap: "whatsapp", requiereSofi: true },
  { to: "/app/documentos", icon: FolderOpen, label: "Documentos", cap: "configuracion" },
  { to: "/app/analitica", icon: BarChart3, label: "Analítica", cap: "agenda" },
  { to: "/app/onboarding", icon: CheckSquare, label: "Mi plan", cap: "configuracion" },
  // CL.3: los pagos de la clínica y su CFDI. `configuracion`, la misma capacidad que exige el
  // backend en /api/billing/*. `soloReal`: la demo pública no tiene facturación (403 en servidor).
  { to: "/app/facturacion", icon: Receipt, label: "Facturación", soloReal: true, cap: "configuracion" },
  { to: "/app/colaboradores", icon: UserCog, label: "Colaboradores", soloReal: true, cap: "colaboradores" },
];

// Los códigos que este menú usa, para que la pantalla de colaboradores pueda contrastarlos
// contra el catálogo real del backend en vez de que nadie los revise nunca.
export const CAPACIDADES_DEL_MENU = Array.from(new Set(nav.map((n) => n.cap)));

// 🔴 COL.12 -- el mapa ruta -> capacidad, DERIVADO del mismo `nav` de arriba y no escrito
//    una segunda vez. `App.js` lo consume para gatear las rutas, así que la regla que
//    esconde una entrada del menú y la que cierra su pantalla son LA MISMA, leída del
//    mismo sitio. Dos listas iguales en dos ficheros son dos listas que se desincronizan
//    -- el defecto de DT.235/DT.247/DT.248 y el motivo por el que el catálogo de las 9
//    capacidades tampoco se escribe en JavaScript.
export const CAPACIDAD_POR_RUTA = Object.fromEntries(nav.map((n) => [n.to, n.cap]));

// TEN.4 -- las rutas que sólo existen con Sofi, DERIVADAS del mismo `nav` (misma razón que arriba:
// esconder la entrada y cerrar la pantalla tienen que ser la misma regla).
export const RUTAS_CON_SOFI = new Set(nav.filter((n) => n.requiereSofi).map((n) => n.to));

export default function AppLayout() {
  // PUENTE-6/7: el proveedor del badge de no leídos vive aquí, no dentro de Inbox.js -- el
  // badge debe verse desde cualquier pantalla del panel (Dashboard, Agenda, etc.), y AppLayout
  // es lo único que persiste montado entre navegaciones dentro de /app/*.
  return (
    <><PruebaGratis /><WizardEntrada /><PlanVigenteGuard><WhatsAppUnreadProvider>
      {/* LIM.5: el consumo de Sofi, pedido una vez y compartido por el aviso de pausa (aquí) y
          la tarjeta del Dashboard. */}
      <ConsumoProvider>
        <AppLayoutInner />
      </ConsumoProvider>
    </WhatsAppUnreadProvider></PlanVigenteGuard></>
  );
}

function AppLayoutInner() {
  const { user, logout, activeClinic, demoInfo, puede } = useAuth();
  const { unreadTotal } = useWhatsAppUnread();
  // Hasta el 13 Sep 2026 este menu se pintaba entero e incondicional -- no habia ningun
  // control de visibilidad, ni por plan ni por demo ni por rol (no existe sistema de roles,
  // DT.237). `soloReal` es el primero: el modulo de gastos maneja dinero real y la demo
  // publica no lo incluye.
  // 🔴 Esta es UNA de las dos capas, no la unica: `pages/Gastos.js` tiene su propio copy
  //    por si alguien llega escribiendo la URL, y la escritura ya la corta el backend con
  //    `_DEMO_ALLOWED_MUTATIONS`. Ocultar el enlace no es proteger nada; es no ofrecer algo
  //    que no se puede usar, para no acabar en el 403 generico que no explica nada (DT.71).
  //
  // COL.10 -- segundo filtro, por CAPACIDAD.
  // 🔴 ESCONDER UNA ENTRADA NO ES UN PERMISO, y conviene tenerlo escrito aquí para que
  //    nadie lo confunda: el servidor ya devuelve 403 en los 117 endpoints, en tres capas
  //    (middleware, censo de arranque y comprobación derivada del router). Este filtro no
  //    protege nada -- sólo evita ofrecer una puerta que está cerrada, para que nadie
  //    choque contra ella y reciba un error que no explica nada (DT.71).
  //    Un frontend manipulado no gana acceso: gana un 403.
  //
  // ⚠️ El menú pasa de 14 entradas planas a 15 (DT.244). NO se reorganiza ni se agrupa
  //    aquí: un bloque hace una cosa. Sólo se oculta lo que no se puede usar.
  const esDemo = !!user?.is_demo_session;
  // TEN.4: tercer filtro, por PLAN. Un plan Sistema no ve Bandeja, Asistente ni Plantillas.
  const planActual = usePlanActual();
  const navWithBadge = nav
    .filter((n) => !n.soloReal || !esDemo)
    .filter((n) => puede(n.cap))
    .filter((n) => !n.requiereSofi || !planActual.sinSofi)
    .map((n) =>
      n.to === "/app/inbox" && unreadTotal > 0 ? { ...n, badge: unreadTotal > 99 ? "99+" : unreadTotal } : n
    );
  const nav2 = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showActivated, setShowActivated] = useState(false);
  const userMenuRef = React.useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    if (searchParams.get("activated") === "true") {
      setShowActivated(true);
      setSearchParams({}, { replace: true });
      const t = setTimeout(() => setShowActivated(false), 5000);
      return () => clearTimeout(t);
    }
  }, []);

  // Close user menu on outside click
  React.useEffect(() => {
    if (!showUserMenu) return;
    function handleClick(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [showUserMenu]);

  return (
    <div className="h-screen flex bg-brand-bg overflow-hidden">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-72 bg-brand-deep border-r border-white/10 flex flex-col transition-transform lg:translate-x-0 lg:relative ${
          open ? "translate-x-0" : "-translate-x-full"
        }`}
        data-testid="app-sidebar"
      >
        <div className="h-16 flex items-center gap-2 px-5 border-b border-white/10">
          <SuritLogo size={32} tone="oscuro" rotulo label="Surit.ai Clínicas" />
          <button className="ml-auto lg:hidden p-1 text-slate-400" onClick={() => setOpen(false)} aria-label="Cerrar menú">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-0.5">
          {navWithBadge.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                  isActive ? "bg-brand-electric text-white font-semibold" : "text-slate-400 hover:bg-white/10 hover:text-white"
                }`
              }
            >
              <n.icon className="w-[18px] h-[18px]" />
              <span className="flex-1">{n.label}</span>
              {n.badge && (
                <span className="text-[10px] font-bold bg-rose-500 text-white px-1.5 py-0.5 rounded-full">{n.badge}</span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/10">
            <div className="w-9 h-9 rounded-full bg-brand-electric text-white grid place-items-center font-semibold text-sm">
              {(user?.name || "U")[0]}
            </div>
            <div className="leading-tight flex-1 min-w-0">
              <div className="text-sm font-medium text-white truncate">{user?.name || "Usuario"}</div>
              <div className="text-[11px] text-slate-400 truncate">{user?.email}</div>
            </div>
            <button
              onClick={() => {
                logout();
                nav2("/");
              }}
              className="text-slate-400 hover:text-white p-1.5 rounded-md hover:bg-white/10"
              title="Cerrar sesión"
              data-testid="app-logout-button"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile backdrop */}
      {open && <div className="fixed inset-0 bg-slate-900/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-16 flex items-center gap-3 px-4 sm:px-6 border-b border-slate-200 bg-white sticky top-0 z-20" data-testid="app-topbar">
          <button className="lg:hidden p-2 text-slate-500" onClick={() => setOpen(true)} aria-label="Abrir menú">
            <Menu className="w-5 h-5" />
          </button>
          <div className="relative max-w-md w-full">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              placeholder="Buscar paciente…"
              className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              data-testid="topbar-search"
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.target.value.trim()) {
                  nav2(`/app/pacientes?q=${encodeURIComponent(e.target.value.trim())}`);
                  e.target.value = "";
                }
              }}
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <ClinicSwitcher />
            <button className="relative p-2 text-slate-500 hover:text-slate-900 rounded-lg hover:bg-slate-100" data-testid="topbar-notifications">
              <Bell className="w-5 h-5" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full bg-rose-500" />
            </button>
            <div className="relative hidden sm:block" ref={userMenuRef}>
              <button
                onClick={() => setShowUserMenu(v => !v)}
                className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-slate-100 cursor-pointer"
                data-testid="topbar-user-menu-trigger"
              >
                <div className="w-8 h-8 rounded-full bg-brand-deep text-white grid place-items-center text-xs font-semibold">
                  {(user?.name || "U")[0]}
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
              {showUserMenu && (
                <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-slate-200 rounded-xl shadow-lg py-1 z-50">
                  <div className="px-4 py-2 border-b border-slate-100">
                    <div className="text-sm font-semibold text-slate-900">{user?.name || "Demo Admin"}</div>
                    <div className="text-xs text-slate-500">{user?.email || "demo@surit.ai"}</div>
                  </div>
                  <button
                    onClick={() => { nav2("/app/perfil"); setShowUserMenu(false); }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    data-testid="topbar-profile-button"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                    Mi perfil
                  </button>
                  <button
                    onClick={() => { logout(); nav2("/"); setShowUserMenu(false); }}
                    className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-50 flex items-center gap-2"
                    data-testid="topbar-logout-button"
                  >
                    <LogOut className="w-4 h-4" /> Cerrar sesión
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Banner de activación post-pago */}
        {showActivated && (
          <div className="flex items-center justify-between gap-3 px-4 py-3 bg-emerald-50 border-b border-emerald-200">
            <div className="flex items-center gap-2 text-sm text-emerald-800 font-medium">
              <svg className="w-4 h-4 text-emerald-600 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              ¡Tu plan está activo! Ya puedes usar todas las funciones de Surit Clínicas.
            </div>
            <button
              onClick={() => setShowActivated(false)}
              className="text-emerald-600 hover:text-emerald-800 p-1 rounded"
              aria-label="Cerrar"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Demo banner — visible debajo del topbar cuando is_demo y no expirado */}
        <DemoBanner demoInfo={demoInfo} />
        <AvisoGracia />

        {/* LIM.5 -- aviso de pausa (B6) para toda sesión; en el Dashboard lo lleva la tarjeta de
            consumo y aquí no se repite. */}
        <AvisoPausa ocultar={location.pathname === "/app" || location.pathname === "/app/"} />

        {/* Page content */}
        <main className="flex-1 overflow-y-auto" data-testid="app-main" key={activeClinic?.id || "no-clinic"}>
          <Outlet />
        </main>
      </div>

      {/* Modal bloqueante cuando la demo expira */}
      {!["/app/onboarding", "/app/reportes"].includes(location.pathname.replace(/\/$/, "")) && (
        <DemoExpiredModal show={demoInfo?.demo_expirado === true} />
      )}
    </div>
  );
}
